import { Routes } from '@angular/router';
import { buildMeinProfilRoute, ErrorMessagePlainComponent, LOCALIZED_SHARED_ROUTES, SHARED_ROUTE_IDS } from '@lexplus-shared-lib';
import { AuthPagesRoutes, IsLoggedInGuard } from '@lexplus-authentication';
import { AdminOnlyGuard } from './guards/admin-only.guard';
import { AdministrationPageRoute } from './features/administration/administration-page.routes';
import { StammdatenPageRoute } from './features/stammdaten/stammdaten-page.routes';
import { KundenPageRoute } from './features/kunden/kunden-page.routes';

/**
 * Localized routes of the admin app. Reuses the shared auth routes (with
 * `isForAdmin: true` → login restricted to the ADMIN role) plus a dashboard guarded
 * by the login and admin guards, and the shared error page. Serves both as the
 * actual router configuration and (via LOCALIZED_APP_ROUTES) as the mapping source
 * for switching language.
 */
export function AdminLocalizedAppRoutes(localeId: string): Routes {
  const locale = localeId?.split('-')[0] || 'en';

  return [
    {
      path: '',
      pathMatch: 'full',
      redirectTo: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.DASHBOARD][locale],
    },
    AuthPagesRoutes(localeId, { isForAdmin: true }),
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.DASHBOARD][locale],
      loadComponent: () => import('./features/dashboard/admin-dashboard-page.component').then((m) => m.AdminDashboardPageComponent),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.DASHBOARD },
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_REGULATIONS][locale],
      loadComponent: () => import('./features/erlasse/pages/erlasse-page/erlasse-page.component').then((m) => m.ErlassePageComponent),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_REGULATIONS },
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_REGULATIONS_INTERNATIONAL][locale],
      loadComponent: () =>
        import('./features/erlasse/pages/internationale-erlasse-page/internationale-erlasse-page.component').then(
          (m) => m.InternationaleErlassePageComponent,
        ),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_REGULATIONS_INTERNATIONAL },
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_REGULATIONS_SUVA_CHECKLISTS][locale],
      loadComponent: () =>
        import('./features/erlasse/pages/suva-checklisten-page/suva-checklisten-page.component').then(
          (m) => m.SuvaChecklistenPageComponent,
        ),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_REGULATIONS_SUVA_CHECKLISTS },
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_COMMENTS][locale],
      loadComponent: () =>
        import('./features/kommentare/pages/kommentare-page/kommentare-page.component').then((m) => m.KommentarePageComponent),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_COMMENTS },
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_CONSULTATIONS][locale],
      loadComponent: () =>
        import('./features/vernehmlassungen/pages/vernehmlassungen-page/vernehmlassungen-page.component').then(
          (m) => m.VernehmlassungenPageComponent,
        ),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_CONSULTATIONS },
    },
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_OBJECTS][locale],
      loadComponent: () =>
        import('./features/themenbereiche/pages/themenbereiche-page/themenbereiche-page.component').then(
          (m) => m.ThemenbereichePageComponent,
        ),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_OBJECTS },
    },
    KundenPageRoute(localeId),
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ADMIN_REPORTS][locale],
      loadComponent: () =>
        import('./features/auswertungen/pages/auswertungen-page/auswertungen-page.component').then((m) => m.AuswertungenPageComponent),
      canActivate: [IsLoggedInGuard, AdminOnlyGuard],
      data: { routeId: SHARED_ROUTE_IDS.ADMIN_REPORTS },
    },
    StammdatenPageRoute(localeId),
    AdministrationPageRoute(localeId),
    buildMeinProfilRoute(locale, [IsLoggedInGuard, AdminOnlyGuard]),
    {
      path: LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ERROR][locale],
      // Referenced directly rather than lazily: lexplus-shared-lib is already in this
      // app's initial bundle (NGXS states, app shell, guards), so a dynamic import of
      // its barrel would emit no separate chunk.
      component: ErrorMessagePlainComponent,
      data: { routeId: SHARED_ROUTE_IDS.ERROR },
    },
    {
      path: '**',
      redirectTo: `${LOCALIZED_SHARED_ROUTES[SHARED_ROUTE_IDS.ERROR][locale]}?errorNumber=404`,
    },
  ];
}
